import React, { useState, useCallback, useEffect } from 'react';
import { fmtDateTimeMedium, todayIso } from '../utils/dateUtils';

const fmtDt = (v) => fmtDateTimeMedium(v);

function PmsGuestLoginAudit({ token, selectedProperty }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fromDate, setFromDate] = useState(todayIso());
  const [toDate, setToDate] = useState(todayIso());
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRoom, setFilterRoom] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 50;

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  const loadData = useCallback(async (pg = 0) => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    setError('');
    try {
      let url = `/api/v1/pms/guest-login-audit?from=${fromDate}&to=${toDate}&limit=${pageSize}&offset=${pg * pageSize}`;
      if (filterStatus) url += `&status=${filterStatus}`;
      if (filterRoom) url += `&room_number=${encodeURIComponent(filterRoom)}`;
      const res = await fetch(url, { headers: headers() });
      const data = await res.json();
      if (res.ok) {
        setRows(data.rows || []);
        setTotal(data.total || 0);
        setPage(pg);
      } else {
        setError(data.error || 'Failed to load');
      }
    } catch (err) {
      setError(err.message || 'Network error');
    }
    setLoading(false);
  }, [selectedProperty, token, headers, fromDate, toDate, filterStatus, filterRoom]);

  useEffect(() => {
    loadData(0);
  }, [selectedProperty?.id]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const statusBadge = (s) => ({
    display: 'inline-block',
    padding: '0.15rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#fff',
    background: s === 'SUCCESS' ? 'var(--theme-success)' : 'var(--theme-danger)',
  });

  const thStyle = { padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid var(--theme-border)', fontSize: '0.82rem', fontWeight: 600, whiteSpace: 'nowrap' };
  const tdStyle = { padding: '0.45rem 0.5rem', borderBottom: '1px solid var(--theme-border-light)', fontSize: '0.84rem', verticalAlign: 'top' };

  return (
    <div style={{ padding: '1rem' }}>
      <h2 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Guest Portal — Login Audit</h2>

      {error && <div className="error-banner">{error}</div>}

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', background: 'var(--theme-bg-light)', padding: '0.7rem 1rem', borderRadius: '8px', border: '1px solid var(--theme-border)' }}>
        <label style={{ fontSize: '0.82rem', fontWeight: 600 }}>From:</label>
        <input type="date" value={fromDate} onChange={e => { const v = e.target.value; setFromDate(v); if (toDate && v && toDate < v) setToDate(v); }} style={{ padding: '0.3rem 0.5rem' }} />
        <label style={{ fontSize: '0.82rem', fontWeight: 600 }}>To:</label>
        <input type="date" value={toDate} min={fromDate || undefined} onChange={e => { const v = e.target.value; setToDate(v); if (fromDate && v && fromDate > v) setFromDate(v); }} style={{ padding: '0.3rem 0.5rem' }} />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: '0.3rem 0.5rem' }}>
          <option value="">All Status</option>
          <option value="SUCCESS">SUCCESS</option>
          <option value="FAILED">FAILED</option>
        </select>
        <input
          type="text"
          placeholder="Room No."
          value={filterRoom}
          onChange={e => setFilterRoom(e.target.value)}
          style={{ padding: '0.3rem 0.5rem', width: '80px' }}
        />
        <button onClick={() => loadData(0)} style={{ padding: '0.35rem 0.8rem', background: 'var(--theme-primary)', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 600 }}>
          Search
        </button>
        <span style={{ fontSize: '0.82rem', color: 'var(--theme-text-secondary)', marginLeft: '0.5rem' }}>{total} record(s)</span>
      </div>

      {loading && <div style={{ color: '#2563eb', marginBottom: '0.5rem' }}>Loading...</div>}

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
          <thead>
            <tr style={{ background: 'var(--theme-primary)', color: '#fff' }}>
              <th style={thStyle}>Time</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Room</th>
              <th style={thStyle}>Guest Name Entered</th>
              <th style={thStyle}>Mobile Last 4</th>
              <th style={thStyle}>Error / Reason</th>
              <th style={thStyle}>Property Code</th>
              <th style={thStyle}>IP Address</th>
              <th style={thStyle}>Browser / Device</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.log_id} style={{ borderBottom: '1px solid var(--theme-border)', background: r.status === 'FAILED' ? '#fef2f2' : undefined }}>
                <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontSize: '0.8rem' }}>{fmtDt(r.created_at)}</td>
                <td style={tdStyle}><span style={statusBadge(r.status)}>{r.status}</span></td>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{r.room_number || '-'}</td>
                <td style={tdStyle}>{r.guest_name_entered || '-'}</td>
                <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: 600 }}>
                  {r.mobile_last4_entered ? `****${r.mobile_last4_entered}` : '-'}
                </td>
                <td style={{ ...tdStyle, color: 'var(--theme-danger)', fontSize: '0.82rem', maxWidth: '300px' }}>{r.error_message || '-'}</td>
                <td style={tdStyle}>{r.property_code || '-'}</td>
                <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.ip_address || '-'}</td>
                <td style={{ ...tdStyle, fontSize: '0.78rem', color: 'var(--theme-text-secondary)', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.user_agent || '-'}
                </td>
              </tr>
            ))}
            {!rows.length && !loading && (
              <tr><td colSpan={9} style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--theme-text-secondary)' }}>No login attempts found for the selected criteria</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > pageSize && (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.8rem', justifyContent: 'center' }}>
          <button disabled={page === 0} onClick={() => loadData(page - 1)}
            style={{ padding: '0.3rem 0.8rem', border: '1px solid #cbd5e0', borderRadius: '5px', cursor: page === 0 ? 'default' : 'pointer', background: '#fff' }}>
            Previous
          </button>
          <span style={{ fontSize: '0.85rem', color: '#334155' }}>
            Page {page + 1} of {totalPages}
          </span>
          <button disabled={page >= totalPages - 1} onClick={() => loadData(page + 1)}
            style={{ padding: '0.3rem 0.8rem', border: '1px solid #cbd5e0', borderRadius: '5px', cursor: page >= totalPages - 1 ? 'default' : 'pointer', background: '#fff' }}>
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default PmsGuestLoginAudit;
